export type SipInvestmentType = "sip" | "lumpsum";

export interface SipCalculatorResult {
  futureValue: number;
  investedAmount: number;
  estimatedReturns: number;
}

export interface YearlyBreakupRow {
  year: number;
  investedAmount: number;
  totalAmount: number;
  interestEarned: number;
}

export const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

export const calculateMonthlySip = (
  monthlyInvestment: number,
  annualReturn: number,
  tenureYears: number
): SipCalculatorResult => {
  const monthlyRate = annualReturn / 12 / 100;
  const months = tenureYears * 12;

  if (monthlyRate === 0) {
    const investedAmount = monthlyInvestment * months;
    return {
      futureValue: roundToOneDecimal(investedAmount),
      investedAmount: roundToOneDecimal(investedAmount),
      estimatedReturns: 0,
    };
  }

  const futureValue =
    monthlyInvestment * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
  const investedAmount = monthlyInvestment * months;

  return {
    futureValue: roundToOneDecimal(futureValue),
    investedAmount: roundToOneDecimal(investedAmount),
    estimatedReturns: roundToOneDecimal(futureValue - investedAmount),
  };
};

export const calculateInvestedAmountOnly = (
  investmentType: SipInvestmentType,
  investment: number,
  tenureYears: number
): number => {
  if (investmentType === "sip") {
    return roundToOneDecimal(investment * tenureYears * 12);
  }

  return roundToOneDecimal(investment);
};

export const calculateLumpsum = (
  investment: number,
  annualReturn: number,
  tenureYears: number
): SipCalculatorResult => {
  const yearlyRate = annualReturn / 100;
  const futureValue = investment * Math.pow(1 + yearlyRate, tenureYears);

  return {
    futureValue: roundToOneDecimal(futureValue),
    investedAmount: roundToOneDecimal(investment),
    estimatedReturns: roundToOneDecimal(futureValue - investment),
  };
};

export const getYearPoints = (tenureYears: number): number[] => {
  const N = Math.round(tenureYears);
  if (N <= 0) return [0];
  if (N === 1) return [0, 1];

  const interval = Math.max(1, Math.round(N / 4));

  const points = [0, interval, 2 * interval, 3 * interval, N];

  return Array.from(new Set(points))
    .filter((y) => y >= 0 && y <= N)
    .sort((a, b) => a - b);
};

export const generateYearlyBreakup = (
  investmentType: SipInvestmentType,
  amount: number,
  annualReturn: number,
  yearPoints: number[]
): YearlyBreakupRow[] => {
  const yearlyRate = annualReturn / 100;

  return yearPoints.map((years) => {
    let investedAmount = 0;
    let totalAmount = 0;

    if (investmentType === "sip") {
      const monthlyRate = yearlyRate / 12;
      const months = years * 12;
      investedAmount = amount * months;
      totalAmount =
        monthlyRate === 0
          ? investedAmount
          : amount * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    } else {
      investedAmount = amount;
      totalAmount = amount * Math.pow(1 + yearlyRate, years);
    }

    return {
      year: years,
      investedAmount: roundToOneDecimal(investedAmount),
      totalAmount: roundToOneDecimal(totalAmount),
      interestEarned: roundToOneDecimal(totalAmount - investedAmount),
    };
  });
};

export const formatCurrency = (value: number, rupeeSymbol = "₹"): string =>
  `${rupeeSymbol}${Math.round(value).toLocaleString("en-IN")}`;
